(() => {
  const VIEW_QUERY_KEY = 'wholesale_view';

  function initVariantToggles(scope) {
    const context = scope instanceof Element ? scope : document;
    const lists = context.querySelectorAll('[data-wholesale-list]');
    if (!lists.length) return;

    lists.forEach((list) => {
      const toggles = list.querySelectorAll('[data-wholesale-list-toggle]');

      toggles.forEach((button) => {
        if (button.dataset.wholesaleListBound === 'true') return;
        button.dataset.wholesaleListBound = 'true';

        button.addEventListener('click', () => {
          const targetId = button.getAttribute('aria-controls');
          if (!targetId) return;

          const target = document.getElementById(targetId);
          const expanded = button.getAttribute('aria-expanded') === 'true';
          const nextState = !expanded;

          button.setAttribute('aria-expanded', String(nextState));
          button.classList.toggle('is-open', nextState);

          if (target) {
            if (nextState) {
              target.removeAttribute('hidden');
              target.hidden = false;
            } else {
              target.setAttribute('hidden', '');
              target.hidden = true;
            }
          }

          const openText = button.dataset.openText || 'View Options';
          const closeText = button.dataset.closeText || 'Hide Options';
          const label = button.querySelector('[data-wholesale-toggle-label]');
          if (label) {
            label.textContent = nextState ? closeText : openText;
          }
        });
      });
    });
  }

  function updateToggleGroups(view) {
    const groups = document.querySelectorAll('[data-wholesale-view-toggle]');
    groups.forEach((group) => {
      group.dataset.currentView = view;
      const defaultView = group.dataset.defaultView || 'grid';

      group.querySelectorAll('[data-view-target]').forEach((button) => {
        const isActive = button.dataset.viewTarget === view;
        button.setAttribute('aria-pressed', String(isActive));
        button.classList.toggle('is-active', isActive);
      });

      group.dataset.defaultView = defaultView;
    });
  }

  function updateViewPanels(view) {
    const panels = document.querySelectorAll('[data-wholesale-view-panel]');
    panels.forEach((panel) => {
      const isActive = panel.dataset.wholesaleViewPanel === view;
      if (isActive) {
        panel.removeAttribute('hidden');
        panel.hidden = false;
      } else {
        panel.setAttribute('hidden', '');
        panel.hidden = true;
      }
    });
  }

  function updateUrlParam(view) {
    const groups = document.querySelectorAll('[data-wholesale-view-toggle]');
    const defaultView = groups.length ? (groups[0].dataset.defaultView || 'grid') : 'grid';
    const url = new URL(window.location.href);

    if (view === defaultView) {
      url.searchParams.delete(VIEW_QUERY_KEY);
    } else {
      url.searchParams.set(VIEW_QUERY_KEY, view);
    }

    history.replaceState({}, '', url.toString());
  }

  function setActiveView(view, options = {}) {
    const { updateUrl = true } = options;
    updateViewPanels(view);
    updateToggleGroups(view);
    if (updateUrl) updateUrlParam(view);
  }

  function initViewToggleGroup(group) {
    const buttons = group.querySelectorAll('[data-view-target]');
    const defaultView = group.dataset.defaultView || 'grid';

    buttons.forEach((button) => {
      const targetView = button.dataset.viewTarget;
      const isAlreadyBound = button.dataset.wholesaleViewBound === 'true';

      const current = group.dataset.currentView || defaultView;
      const isActive = targetView === current;
      button.setAttribute('aria-pressed', String(isActive));
      button.classList.toggle('is-active', isActive);

      if (isAlreadyBound) return;
      button.dataset.wholesaleViewBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (!targetView || targetView === group.dataset.currentView) return;
        setActiveView(targetView);
      });
    });
  }

  function initViewToggles(scope) {
    const context = scope instanceof Element ? scope : document;
    const groups = context.querySelectorAll('[data-wholesale-view-toggle]');
    if (!groups.length) return;

    groups.forEach(initViewToggleGroup);

    const initialGroup = groups[0];
    const initialView = initialGroup.dataset.currentView || initialGroup.dataset.defaultView || 'grid';
    setActiveView(initialView, { updateUrl: false });
  }

  function stripWholesaleSuffix(scope) {
    const context = scope instanceof Element ? scope : document;
    const titles = context.querySelectorAll('.banner__title');
    if (!titles.length) return;

    titles.forEach((title) => {
      const splitWords = title.querySelector('split-words');

      if (splitWords) {
        const words = Array.from(splitWords.querySelectorAll('.word'));
        if (!words.length) return;

        const lastWord = words[words.length - 1];
        const wordText = lastWord?.dataset?.word || lastWord?.textContent || '';
        if (!/\bws$/i.test(wordText.trim())) return;

        const whitespaceSibling = lastWord.previousElementSibling;
        if (whitespaceSibling && whitespaceSibling.classList.contains('whitespace')) {
          whitespaceSibling.remove();
        }
        lastWord.remove();

        const remainingWords = splitWords.querySelectorAll('.word').length;
        if (remainingWords > 0) {
          splitWords.style.setProperty('--word-total', remainingWords);
        }
        return;
      }

      const currentText = title.textContent || '';
      if (!/\bws$/i.test(currentText.trim())) return;
      title.textContent = currentText.replace(/\s+ws$/i, '');
    });
  }

  /**
   * Handle variant changes in wholesale product cards
   * Updates price, SKU, and variant ID when user selects different options
   */
  function initWholesaleVariantPickers(scope) {
    const context = scope instanceof Element ? scope : document;
    const pickers = context.querySelectorAll('variant-picker[id^="VariantPicker-Wholesale-"]');
    if (!pickers.length) return;

    pickers.forEach((picker) => {
      if (picker.dataset.wholesaleVariantBound === 'true') return;
      picker.dataset.wholesaleVariantBound = 'true';

      const excludedVariants = picker.dataset.excludedVariants ? picker.dataset.excludedVariants.split(',').map(id => id.trim()) : [];

      // Get product JSON embedded in the picker
      const productScript = picker.querySelector('[data-product-json]');
      if (!productScript) {
        console.error('No product JSON found in picker');
        return;
      }

      const product = JSON.parse(productScript.textContent);
      const wholesaleDiscount = getWholesaleDiscountPercent(product);

      console.log('Initialized wholesale variant picker for:', product.title, 'with', wholesaleDiscount + '% discount');

      // Get references to elements that need updating
      // Note: price and SKU are siblings of the form, not inside it
      const productCard = picker.closest('.product-card');
      const priceContainer = productCard?.querySelector('.price');
      const skuElement = productCard?.querySelector('.product-card__sku');
      const variantIdInput = picker.closest('form')?.querySelector('input[name="id"]');
      const addToCartButton = picker.closest('form')?.querySelector('button[type="submit"]');
      const quantityInput = picker.closest('form')?.querySelector('input[name="quantity"]');

      picker.addEventListener('change', () => {
        // Get all selected option values
        const selects = picker.querySelectorAll('select');
        const selectedOptions = Array.from(selects).map(select => select.value);

        console.log('Selected options:', selectedOptions);

        // Find matching variant in product data
        const variant = product.variants.find(v => {
          return selectedOptions.every((optionValue, index) => {
            return v.options[index] === optionValue;
          });
        });

        console.log('Found variant:', variant);

        if (variant) {
          // Check if variant is excluded from wholesale
          const isExcluded = excludedVariants.includes(String(variant.id));

          if (isExcluded) {
            console.warn('Variant', variant.id, 'is excluded from wholesale');
            // Find first non-excluded variant
            const nonExcludedVariant = product.variants.find(v => !excludedVariants.includes(String(v.id)));

            if (nonExcludedVariant) {
              // Reset selects to match the non-excluded variant
              updateSelectsForVariant(selects, nonExcludedVariant, product);
              updateCardWithVariant(nonExcludedVariant, wholesaleDiscount, {
                priceContainer,
                skuElement,
                variantIdInput,
                addToCartButton,
                quantityInput
              });
            }
          } else {
            updateCardWithVariant(variant, wholesaleDiscount, {
              priceContainer,
              skuElement,
              variantIdInput,
              addToCartButton,
              quantityInput
            });
          }
        }
      });

      // Trigger initial update to ensure correct price is displayed (overriding Liquid/Server rendered price)
      const selects = picker.querySelectorAll('select');
      const selectedOptions = Array.from(selects).map(select => select.value);

      const currentVariant = product.variants.find(v => {
        return selectedOptions.every((optionValue, index) => {
          return v.options[index] === optionValue;
        });
      });

      if (currentVariant) {
        updateCardWithVariant(currentVariant, wholesaleDiscount, {
          priceContainer,
          skuElement,
          variantIdInput,
          addToCartButton,
          quantityInput
        });
      }
    });
  }

  /**
   * Extract wholesale discount percentage from product tags
   * Looks for tags like "WS40" (40% off), "WS30" (30% off), etc.
   */
  function getWholesaleDiscountPercent(product) {
    if (!product || !product.tags) return 0;

    // Determine customer type
    const customer = window.theme?.customer;
    let isBronze = false;

    if (customer?.tags && Array.isArray(customer.tags)) {
      isBronze = customer.tags.some(tag => tag.toLowerCase() === 'bronzewholesale');
    }

    // Discount Mapping Configuration
    // Format: Standard Wholesale % => Bronze Wholesale %
    const bronzeMapping = {
      20: 15,
      30: 20, 25: 20, 35: 20, 27: 20,
      40: 25, 32: 25,
      50: 30, 45: 30, 46: 30, 41: 30
    };

    for (const tag of product.tags) {
      const tagUpper = tag.toUpperCase();
      // Always look for standard 'WS' tags
      if (tagUpper.startsWith('WS')) {
        const valStr = tagUpper.substring(2); // Remove 'WS'
        // Validate it's a number
        if (/^\d+$/.test(valStr)) {
          let val = parseInt(valStr, 10);
          if (val > 0 && val <= 100) {
            // If customer is Bronze, map the discount
            if (isBronze) {
              if (bronzeMapping.hasOwnProperty(val)) {
                return bronzeMapping[val];
              } else {
                return 0; // No mapping found = no discount
              }
            }
            return val;
          }
        }
      }
    }
    return 0;
  }

  /**
   * Calculate wholesale discounted price
   */
  function calculateWholesalePrice(price, discountPercent) {
    if (discountPercent <= 0) return price;
    const discountMultiplier = (100 - discountPercent) / 100;
    return Math.round(price * discountMultiplier);
  }

  /**
   * Update select elements to match a specific variant
   */
  function updateSelectsForVariant(selects, variant, product) {
    if (!variant || !product) return;

    variant.options.forEach((optionValue, index) => {
      const select = selects[index];
      if (select) {
        const option = Array.from(select.options).find(opt => opt.value === optionValue);
        if (option) {
          select.value = optionValue;
        }
      }
    });
  }

  /**
   * Update product card elements with new variant data
   */
  function updateCardWithVariant(variant, wholesaleDiscount, elements) {
    const { priceContainer, skuElement, variantIdInput, addToCartButton, quantityInput } = elements;

    console.log('updateCardWithVariant called');
    console.log('- variant:', variant);
    console.log('- wholesaleDiscount:', wholesaleDiscount);
    console.log('- priceContainer:', priceContainer);

    // Update SKU
    if (skuElement && variant.sku) {
      skuElement.textContent = `SKU: ${variant.sku}`;
      skuElement.style.display = '';
      console.log('✓ SKU updated to:', variant.sku);
    } else if (skuElement) {
      skuElement.style.display = 'none';
      console.log('✓ SKU hidden (no SKU for variant)');
    }

    // Update price with wholesale discount applied
    if (!priceContainer) {
      console.error('❌ Price container not found!');
      return;
    }

    if (variant.price !== undefined) {
      const originalPrice = variant.price;
      const discountedPrice = calculateWholesalePrice(originalPrice, wholesaleDiscount);
      const hasWholesaleDiscount = discountedPrice < originalPrice;

      const priceElement = priceContainer.querySelector('.price__regular');
      console.log('- priceElement:', priceElement);
      if (priceElement) {
        const formattedPrice = formatMoney(discountedPrice);
        console.log('✓ Updating price to:', formattedPrice, '(from', originalPrice, 'cents)');
        priceElement.innerHTML = formattedPrice;
      } else {
        console.error('❌ .price__regular not found inside price container');
      }

      // Show original price as strikethrough if wholesale discount applies
      if (hasWholesaleDiscount) {
        let saleElement = priceContainer.querySelector('.price__sale');
        if (!saleElement) {
          // Create screen reader text for sale price
          const srText = document.createElement('span');
          srText.className = 'sr-only';
          srText.textContent = 'Regular price';
          priceContainer.insertBefore(srText, priceElement);

          saleElement = document.createElement('span');
          saleElement.className = 'price__sale inline-flex items-center h-auto relative';
          priceContainer.appendChild(saleElement);
        }
        saleElement.innerHTML = formatMoney(originalPrice);
        priceContainer.classList.add('price--on-sale');
      } else if (variant.compare_at_price && variant.compare_at_price > originalPrice) {
        // Handle regular Shopify compare_at_price
        let saleElement = priceContainer.querySelector('.price__sale');
        if (!saleElement) {
          saleElement = document.createElement('span');
          saleElement.className = 'price__sale inline-flex items-center h-auto relative';
          priceContainer.appendChild(saleElement);
        }
        saleElement.innerHTML = formatMoney(variant.compare_at_price);
        priceContainer.classList.add('price--on-sale');
      } else {
        // Remove sale price element if no discount
        const saleElement = priceContainer.querySelector('.price__sale');
        if (saleElement) {
          saleElement.remove();
        }
        const srText = priceContainer.querySelector('.sr-only');
        if (srText) {
          srText.remove();
        }
        priceContainer.classList.remove('price--on-sale');
      }
    }

    // Update hidden variant ID input
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    // Update add to cart button
    if (addToCartButton) {
      addToCartButton.dataset.variantId = variant.id;

      if (variant.available) {
        addToCartButton.disabled = false;
        const btnText = addToCartButton.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = addToCartButton.closest('form')?.querySelector('[data-pre-order]')
            ? (window.theme?.variantStrings?.preOrder || 'Pre-order')
            : (window.theme?.variantStrings?.addToCart || 'Add to cart');
        }
      } else {
        addToCartButton.disabled = true;
        const btnText = addToCartButton.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = window.theme?.variantStrings?.soldOut || 'Sold out';
        }
      }
    }

    // Update quantity input data attributes
    if (quantityInput) {
      quantityInput.dataset.quantityVariantId = variant.id;
    }
  }

  /**
   * Format price using Shopify's money format
   * Falls back to basic formatting if theme money format not available
   */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  function init(scope) {
    initVariantToggles(scope);
    initViewToggles(scope);
    stripWholesaleSuffix(scope);
    initWholesaleVariantPickers(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('collection:reloaded', () => init(document));

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
